/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Clock } from 'lucide-react';
import { CloseIcon } from '@/components/icons';
import { searchUrlBuilder } from '@/lib/url';
import { useTranslation } from 'react-i18next';

interface RecentSearchesProps {
    recentSearches?: string[];
    closeAndNavigate: (link: string) => void;
    clearRecentSearches: () => void;
    removeRecentSearch: (search: string) => void;
}

/**
 * Saved searches shown in the header search panel below the suggestions' minimum length (PRIC-66 CA7). Styled like
 * the suggestions dropdown; each entry can be removed on its own, as on priceshoes.com.
 */
export default function RecentSearches({
    recentSearches = [],
    closeAndNavigate,
    clearRecentSearches,
    removeRecentSearch,
}: RecentSearchesProps) {
    const { t } = useTranslation('search');

    if (recentSearches.length === 0) {
        return null;
    }

    return (
        <section className="flex flex-col gap-2 px-4 py-4 md:gap-3 md:px-6" aria-labelledby="recent-searches-title">
            <h2 id="recent-searches-title" className="text-sm font-medium leading-[22px] text-ps-black md:text-lg">
                {t('suggestions.recentSearches')}
            </h2>
            <ul className="flex flex-col gap-1">
                {recentSearches.map((recentSearch) => (
                    <li key={recentSearch} className="flex items-center gap-2">
                        <button
                            type="button"
                            data-slot="suggestion"
                            onClick={() => closeAndNavigate(searchUrlBuilder(recentSearch))}
                            className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1 text-left text-xs leading-[22px] text-ps-black hover:underline md:text-base">
                            <Clock className="size-4 shrink-0 text-ps-gray-500" aria-hidden="true" />
                            <span className="truncate">{recentSearch}</span>
                        </button>
                        <button
                            type="button"
                            data-slot="suggestion"
                            onClick={() => removeRecentSearch(recentSearch)}
                            aria-label={t('suggestions.removeRecentSearch', { search: recentSearch })}
                            className="flex shrink-0 cursor-pointer items-center justify-center rounded-full p-1 text-ps-gray-500 hover:bg-ps-hover-primary hover:text-ps-text-label">
                            <CloseIcon className="size-4" />
                        </button>
                    </li>
                ))}
            </ul>
            <button
                type="button"
                data-slot="suggestion"
                onClick={clearRecentSearches}
                className="cursor-pointer self-start text-xs leading-[22px] text-ps-text-link hover:underline">
                {t('suggestions.clearRecentSearches')}
            </button>
        </section>
    );
}
